import { usePathname } from 'next/navigation'
import { ACTION_TYPES, MOBILE_EVENTS, PLUS_APP_VERSION } from '@/consts'
import { checkOldAppVersion, isWebview, postMessageToApp } from '@/utils'
import { loginWithKakao } from '@/utils/kakao'
import { loginWithNaver } from '@/utils/naver'
import useKakaoSdk from '../useKakaoSdk'

interface Props {
  redirectPath?: string
}

const IN_APP_PURCHASE_TEST_URL =
  'lunit-care-git-lcsd-5339-inapp-purchase-test-lunit-care.vercel.app'

/**
 * 카카오, 네이버 로그인을 위한 커스텀 훅
 * @example
 * const { handleKakaoLogin, handleNaverLogin } = useSocialLogin()
 * @returns handleKakaoLogin, handleNaverLogin 함수 객체 반환 (각각 카카오, 네이버 로그인 함수)
 */
const useSocialLogin = () => {
  const isApp = isWebview()
  const pathname = usePathname()
  useKakaoSdk() // 카카오 sdk 로드

  const isPlusApp = isApp && !checkOldAppVersion(PLUS_APP_VERSION)

  /**
   * 카카오 로그인 함수
   * @param redirectPath 로그인 이후 리다이렉트할 경로
   */
  const handleKakaoLogin = ({ redirectPath = pathname }: Props) => {
    // 결제 테스트를 위해, 해당 프리뷰에서는 앱에서도 웹로그인을 한다.
    // TODO: 인앱 결제 테스트 완료 후 제거
    if (
      isPlusApp &&
      typeof window !== 'undefined' &&
      window?.location?.host === IN_APP_PURCHASE_TEST_URL
    ) {
      loginWithKakao({ redirectPath })
      return
    }

    if (isApp) {
      const shouldCreateDocument = redirectPath.includes(
        ACTION_TYPES.CREATE_BOARD,
      )
      postMessageToApp(MOBILE_EVENTS.KAKAO_LOGIN, {
        shouldCreateDocument,
        redirectPath,
      })
    } else {
      loginWithKakao({ redirectPath })
    }
  }

  /**
   * 네이버 로그인 함수
   * @param redirectPath 로그인 이후 리다이렉트할 경로
   */
  const handleNaverLogin = ({ redirectPath = pathname }: Props) => {
    if (isApp) {
      const shouldCreateDocument = redirectPath.includes(
        ACTION_TYPES.CREATE_BOARD,
      )
      postMessageToApp(MOBILE_EVENTS.NAVER_LOGIN, {
        shouldCreateDocument,
        redirectPath,
      })
    } else {
      loginWithNaver({ redirectPath })
    }
  }

  return { handleKakaoLogin, handleNaverLogin }
}

export default useSocialLogin
